import { Component, OnInit } from '@angular/core';
import { SortDescriptor } from '@progress/kendo-data-query';
import _, { isArray } from 'lodash';
import { getRiskTypeIcon } from 'src/app/shared/helpers/reactive-form.class';
import { SortOrder } from 'src/app/shared/modules/graphql/enums/generic.enums';
import { IApiContactFilterType, IApiInvestigation, IApiInvestigationFilter, IApiInvestigationFilterType, IApiInvestigationOrderBy, IApiInvestigationRoleNames, IApiInvestigationStaff } from 'src/app/shared/modules/graphql/types/types';
import { LoaderService } from 'src/app/shared/modules/loader/loader.service';
import { InvestigationService } from 'src/app/shared/services';
import { ClosedInvestigationDataSource } from 'src/app/shared/services/investigation/investigation.datasource';
import { InvestigationStatusNames } from '../dashboard-assigned-table/dashboard-assigned-table.component';
import { AddCommentModalComponent } from '../add-comment-modal/add-comment-modal.component';
import { DialogRef, DialogService } from '@progress/kendo-angular-dialog';
import { SharedService } from 'src/app/common/shared.service';
import { TABS } from '../dashboard-tabs/dashboard-tabs.component';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
  selector: 'app-dashboard-closed-investigations',
  templateUrl: './dashboard-closed-investigations.component.html',
  styleUrls: ['./dashboard-closed-investigations.component.scss']
})
export class DashboardClosedInvestigationsComponent implements OnInit {
  private _dataSource: ClosedInvestigationDataSource;
  private _filters: IApiInvestigationFilter[] = [];
  public filterTypes = IApiInvestigationFilterType;
  public investigationStatusNames = InvestigationStatusNames;

  public get dataSource() {
    return this._dataSource;
  }
  public set dataSource(val) {
    this._dataSource = val;
  }

  public investigations: IApiInvestigation[];

  public sort: SortDescriptor[] = [{
    field: IApiInvestigationOrderBy.NefcoNumber,
    dir: 'desc'
  }];

  public get filters() {
    return this._filters;
  }
  public set filters(val) {
    this._filters = val;
  }

  public searchValue = '';
  public get pageOptions() {
    if (!this._dataSource) return null;
    return this._dataSource.listPage;
  }

  public contactFilters = [{
    type: IApiContactFilterType.ClosedInvestigation,
    value: "true"
  }];

  constructor(
    private investigationService: InvestigationService,
    private loaderService: LoaderService,
    private dialogService: DialogService,
    private sharedService: SharedService,
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) {

  }

  public async ngOnInit() {
    this.dataSource = new ClosedInvestigationDataSource(this.investigationService);
    this.loaderService.attachObservable(this.dataSource.loading$);
    this.dataSource.listPage.orderBy = IApiInvestigationOrderBy.NefcoNumber;
    this.dataSource.listPage.sortOrder = SortOrder.DESC;
    this.dataSource?.contents$.subscribe((res) => {
      if (res && res?.length) {
        res.map((obj) => {
          obj['assigned'] = this.orderInvestigationStaff(obj?.InvestigationStaff);
        });
      }

      this.investigations = res;
    });
  }

  public orderInvestigationStaff(investigationStaff: IApiInvestigationStaff[]) {
    // Order investigation staff correctly
    // 1) Lead Investigator 2) Lead Tech Reviewer/Engineer 3) Remaining staff, alpha order
    if (investigationStaff && investigationStaff.length) {
      // First sort by created Date
      // This way we find the correct investigator / tech reviewer
      investigationStaff = investigationStaff.sort((a, b) => a.createdAt < b.createdAt ? 1 : -1);
      const staff = [];
      // Then find lead investigator / tech reviewer
      const leadInvestigator = investigationStaff.find((obj) => obj.Role.title === IApiInvestigationRoleNames.Investigator && obj.isPrimary === true)
        || investigationStaff.find((obj) => obj.Role.title === IApiInvestigationRoleNames.Investigator);
      const leadTechOnly = investigationStaff.find((obj) => obj.Role.title === IApiInvestigationRoleNames.TechReviewer && obj.isPrimary === true)
        || investigationStaff.find((obj) => obj.Role.title === IApiInvestigationRoleNames.TechReviewer);

      if (leadInvestigator) staff.push(leadInvestigator);
      if (leadTechOnly) staff.push(leadTechOnly);
      return staff;
    } else {
      return [];
    }
  }

  public sortChange = (e) => {
    this.sort = e;
    if (e && e?.[0]?.dir) {
      this.dataSource.listPage.orderBy = e?.[0]?.field;
      this.dataSource.listPage.sortOrder = e?.[0]?.dir === 'asc' ? SortOrder.ASC : SortOrder.DESC;
    } else {
      this.dataSource.listPage.orderBy = IApiInvestigationOrderBy.NefcoNumber;
      this.dataSource.listPage.sortOrder = SortOrder.DESC;
    }
    this.load();
  }

  public loadFilters(filterList: IApiInvestigationFilter[], load = true) {
    // If no new filters, return
    if (_.isEqual(filterList, this.dataSource.lastFilters)) return;
    this.dataSource.lastFilters.forEach(({ type }) =>
      this.dataSource.removeFilter(type)
    ); // Remove previous filters and only apply the ones that came in
    filterList.forEach(({ type, value }) =>
      this.dataSource.applyFilter(type, value)
    );
    if (load) this.load();
  }

  public filterValue(filter: IApiInvestigationFilterType) {
    // Need to parse true/false strings so they aren't misinterpreted by truthy/falsy
    const value = this.filters.find(({ type }) => type === filter)?.value;
    return (value === "true" || value === "false") ? JSON.parse(value) : (value) ? value : null;
  }

  public setFilters(value: string | undefined, type: IApiInvestigationFilterType) {
    const hasValue = (val: any) => (val !== undefined) || (val !== null); // We can have falsy values for some filters, so permit those but not undefined/null
    const filtersCopy = this.filters.filter(f => f.type !== type);
    this.filters = hasValue(value) ? [...filtersCopy, {
      type: type,
      value: isArray(value) && value?.length ? JSON.stringify(value) : !value?.length ? null : value
    }] : filtersCopy;
  }

  public clearFilter() {
    this.filters = [];
    this.dataSource.data = [];
    this.applyFilter()
  }

  public applyFilter() {
    if (this.filters?.length) {
      this.loadFilters(this.filters);
      this.load();
    }
  }

  load() {
    this.dataSource.pagingReset();
    this.dataSource.load();
  }

  public filterByIdValue(filter: IApiInvestigationFilterType) {
    // Need to parse true/false strings so they aren't misinterpreted by truthy/falsy
    const value = this.filters.find(({ type }) => type === filter)?.value;
    return value ? JSON.parse(value) : null;
  }

  public getIcon(RiskTypeId: string) {
    return getRiskTypeIcon(RiskTypeId);
  }

  public openCommentModal(investigation: IApiInvestigation) {
    const dialog: DialogRef = this.dialogService.open({
      content: AddCommentModalComponent,
      preventAction: (ev) => {
        return ev !== ("closed" as any);
      },
    });

    const dialogInstance = dialog.content.instance as AddCommentModalComponent;
    dialogInstance.data = investigation;
    dialog.result.subscribe((result) => {
      if (result === true) {
        this.load()
      }
    });
  }
}
